import { useEffect, useState } from "react"
import { onAuthStateChanged, type User } from "firebase/auth"

import { auth } from "@/features/auth/providers/firebase"

import { userService } from "../api/user-service"
import type { UserProfile } from "../types"

interface UserProfileState {
  profile: UserProfile | null
  loading: boolean
  error: boolean
}

const PROFILE_CACHE_TTL_MS = 60_000

let profileCache: { uid: string | null; profile: UserProfile | null; fetchedAt: number } = {
  uid: null,
  profile: null,
  fetchedAt: 0,
}

type ProfileCacheListener = (uid: string, profile: UserProfile | null) => void
const profileCacheListeners = new Set<ProfileCacheListener>()

let inFlightRequest: { uid: string; promise: Promise<UserProfile | null> } | null = null

const isProfileCacheFresh = (uid: string): boolean =>
  profileCache.uid === uid && Date.now() - profileCache.fetchedAt < PROFILE_CACHE_TTL_MS

const readCachedProfile = (uid: string): UserProfile | null =>
  isProfileCacheFresh(uid) ? profileCache.profile : null

/** Write `/me` into the in-memory cache and notify mounted `useUserProfile` callers. */
export function writeUserProfileCache(uid: string, profile: UserProfile | null): void {
  profileCache = { uid, profile, fetchedAt: Date.now() }
  profileCacheListeners.forEach((listener) => listener(uid, profile))
}

const fetchProfile = async (user: User): Promise<UserProfile | null> => {
  const cached = readCachedProfile(user.uid)
  if (cached !== null || isProfileCacheFresh(user.uid)) {
    return cached
  }

  if (inFlightRequest?.uid === user.uid) {
    return inFlightRequest.promise
  }

  const request = (async () => {
    const token = await user.getIdToken()
    const profile = await userService.getMe(token)
    writeUserProfileCache(user.uid, profile)
    return profile
  })()

  inFlightRequest = { uid: user.uid, promise: request }

  try {
    return await request
  } finally {
    if (inFlightRequest?.uid === user.uid) {
      inFlightRequest = null
    }
  }
}

/**
 * Loads the signed-in user's backend profile (`/v1/users/me`).
 * Returns null when the profile doesn't exist yet.
 */
export function useUserProfile(): UserProfileState {
  const currentUser = auth.currentUser
  const cachedProfile = currentUser ? readCachedProfile(currentUser.uid) : null

  const [state, setState] = useState<UserProfileState>(() =>
    userService.isConfigured
      ? {
          profile: cachedProfile,
          loading: currentUser ? !isProfileCacheFresh(currentUser.uid) : true,
          error: false,
        }
      : { profile: null, loading: false, error: false },
  )

  useEffect(() => {
    if (!userService.isConfigured) return
    let cancelled = false

    const load = async (user: User | null): Promise<void> => {
      if (!user) {
        if (!cancelled) setState({ profile: null, loading: false, error: false })
        return
      }

      const cached = readCachedProfile(user.uid)
      if (cached !== null || isProfileCacheFresh(user.uid)) {
        if (!cancelled) setState({ profile: cached, loading: false, error: false })
        return
      }

      if (!cancelled) {
        setState({ profile: null, loading: true, error: false })
      }

      try {
        const profile = await fetchProfile(user)
        if (!cancelled) setState({ profile, loading: false, error: false })
      } catch {
        if (!cancelled) setState({ profile: null, loading: false, error: true })
      }
    }

    const unsubscribe = onAuthStateChanged(auth, (user) => {
      void load(user)
    })

    const onCacheWrite = (uid: string, profile: UserProfile | null) => {
      if (cancelled || auth.currentUser?.uid !== uid) return
      setState({ profile, loading: false, error: false })
    }
    profileCacheListeners.add(onCacheWrite)

    return () => {
      cancelled = true
      unsubscribe()
      profileCacheListeners.delete(onCacheWrite)
    }
  }, [])

  return state
}
